import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0, has_more: false });
  
  // Filters
  const [search, setSearch] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('all'); // 'all', 'true', 'false'
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  
  // Delete confirmation
  const [deleteModal, setDeleteModal] = useState({ show: false, user: null, hardDelete: false });

  useEffect(() => {
    fetchUsers();
  }, [pagination.offset, search, isActiveFilter, sortBy, sortOrder, includeDeleted]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        limit: pagination.limit,
        offset: pagination.offset,
        sort_by: sortBy,
        sort_order: sortOrder,
        include_deleted: includeDeleted,
      };
      
      if (search) params.search = search;
      if (isActiveFilter !== 'all') params.is_active = isActiveFilter === 'true';
      
      const data = await apiService.getAdminUsers(params);
      setUsers(data.users || []);
      setPagination(data.pagination || { total: 0, limit: 20, offset: 0, has_more: false });
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, offset: 0 })); // Reset to first page
  };

  const handleDelete = async () => {
    if (!deleteModal.user) return;
    
    try {
      await apiService.deleteAdminUser(deleteModal.user.id, deleteModal.hardDelete);
      setDeleteModal({ show: false, user: null, hardDelete: false });
      fetchUsers(); // Refresh list
    } catch (err) {
      alert(`Error deleting user: ${err.message}`);
    }
  };

  const handlePageChange = (newOffset) => {
    setPagination(prev => ({ ...prev, offset: newOffset }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div style={styles.container}>
      <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
      
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.titleSection}>
            <button style={styles.backButton} onClick={() => navigate('/admin')}>
              <i className="fas fa-arrow-left"></i>
            </button>
            <div>
              <h1 style={styles.title}>Manage Users</h1>
              <p style={styles.subtitle}>{pagination.total} total users</p>
            </div>
          </div>
          <button style={styles.createButton} onClick={() => navigate('/admin/users/create')}>
            <i className="fas fa-plus" style={styles.buttonIcon}></i>
            Create User
          </button>
        </div>
      </div>

      <div style={styles.filtersCard}>
        <div style={styles.searchBar}>
          <i className="fas fa-search" style={styles.searchIcon}></i>
          <input
            type="text"
            placeholder="Search by email or username..."
            value={search}
            onChange={handleSearchChange}
            style={styles.searchInput}
          />
        </div>
        
        <div style={styles.filters}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Status:</label>
            <select 
              value={isActiveFilter} 
              onChange={(e) => { setIsActiveFilter(e.target.value); setPagination(prev => ({...prev, offset: 0})); }}
              style={styles.filterSelect}
            >
              <option value="all">All Users</option>
              <option value="true">Active Only</option>
              <option value="false">Inactive Only</option>
            </select>
          </div>
          
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Sort By:</label>
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="created_at">Created Date</option>
              <option value="email">Email</option>
              <option value="username">Username</option>
            </select>
          </div>
          
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Order:</label>
            <select 
              value={sortOrder} 
              onChange={(e) => setSortOrder(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>
          
          <div style={styles.checkboxGroup}>
            <input
              type="checkbox"
              id="includeDeleted"
              checked={includeDeleted}
              onChange={(e) => { setIncludeDeleted(e.target.checked); setPagination(prev => ({...prev, offset: 0})); }}
              style={styles.checkbox}
            />
            <label htmlFor="includeDeleted" style={styles.checkboxLabel}>Include Deleted</label>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading users...</p>
        </div>
      ) : error ? (
        <div style={styles.errorContainer}>
          <i className="fas fa-exclamation-triangle" style={styles.errorIcon}></i>
          <p style={styles.errorText}>{error}</p>
          <button style={styles.retryButton} onClick={fetchUsers}>Retry</button>
        </div>
      ) : users.length === 0 ? (
        <div style={styles.emptyState}>
          <i className="fas fa-users" style={styles.emptyIcon}></i>
          <p style={styles.emptyText}>No users found</p>
        </div>
      ) : (
        <>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeaderRow}>
                  <th style={styles.tableHeader}>ID</th>
                  <th style={styles.tableHeader}>Email</th>
                  <th style={styles.tableHeader}>Username</th>
                  <th style={styles.tableHeader}>Role</th>
                  <th style={styles.tableHeader}>Status</th>
                  <th style={styles.tableHeader}>Created</th>
                  <th style={styles.tableHeader}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} style={styles.tableRow} className="table-row">
                    <td style={styles.tableCell}>{user.id}</td>
                    <td style={styles.tableCell}>{user.email}</td>
                    <td style={styles.tableCell}>{user.username || 'N/A'}</td>
                    <td style={styles.tableCell}>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: user.is_admin ? '#6f42c1' : '#2c5aa0',
                      }}>
                        {user.is_admin ? 'Admin' : 'User'}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      {user.deleted_at ? (
                        <span style={{...styles.badge, backgroundColor: '#dc3545'}}>Deleted</span>
                      ) : user.is_active ? (
                        <span style={{...styles.badge, backgroundColor: '#28a745'}}>Active</span>
                      ) : (
                        <span style={{...styles.badge, backgroundColor: '#ffc107', color: '#000'}}>Inactive</span>
                      )}
                    </td>
                    <td style={styles.tableCell}>{formatDate(user.created_at)}</td>
                    <td style={styles.tableCell}>
                      <div style={styles.actionButtons}>
                        <button
                          style={styles.actionButton}
                          onClick={() => navigate(`/admin/users/${user.id}`)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        {!user.deleted_at && (
                          <button
                            style={{...styles.actionButton, color: '#dc3545'}}
                            onClick={() => setDeleteModal({ show: true, user, hardDelete: false })}
                            title="Delete User"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={styles.pagination}>
            <div style={styles.paginationInfo}>
              Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
            </div>
            <div style={styles.paginationButtons}>
              <button
                style={{...styles.paginationButton, ...(pagination.offset === 0 ? styles.paginationButtonDisabled : {})}}
                onClick={() => handlePageChange(0)}
                disabled={pagination.offset === 0}
              >
                <i className="fas fa-angle-double-left"></i>
              </button>
              <button
                style={{...styles.paginationButton, ...(pagination.offset === 0 ? styles.paginationButtonDisabled : {})}}
                onClick={() => handlePageChange(Math.max(0, pagination.offset - pagination.limit))}
                disabled={pagination.offset === 0}
              >
                <i className="fas fa-angle-left"></i>
              </button>
              <span style={styles.paginationText}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                style={{...styles.paginationButton, ...(!pagination.has_more ? styles.paginationButtonDisabled : {})}}
                onClick={() => handlePageChange(pagination.offset + pagination.limit)}
                disabled={!pagination.has_more}
              >
                <i className="fas fa-angle-right"></i>
              </button>
              <button
                style={{...styles.paginationButton, ...(!pagination.has_more ? styles.paginationButtonDisabled : {})}}
                onClick={() => handlePageChange((totalPages - 1) * pagination.limit)}
                disabled={!pagination.has_more}
              >
                <i className="fas fa-angle-double-right"></i>
              </button>
            </div>
          </div>
        </>
      )}

      {deleteModal.show && (
        <div style={styles.modalOverlay} onClick={() => setDeleteModal({ show: false, user: null, hardDelete: false })}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Confirm Delete</h3>
            <p style={styles.modalText}>
              Are you sure you want to delete user <strong>{deleteModal.user?.email}</strong>?
            </p>
            <div style={styles.checkboxGroup}>
              <input
                type="checkbox"
                id="hardDelete"
                checked={deleteModal.hardDelete}
                onChange={(e) => setDeleteModal(prev => ({ ...prev, hardDelete: e.target.checked }))}
                style={styles.checkbox}
              />
              <label htmlFor="hardDelete" style={styles.checkboxLabel}>
                Hard delete (permanently remove from database)
              </label>
            </div>
            <div style={styles.modalButtons}>
              <button
                style={styles.modalCancelButton}
                onClick={() => setDeleteModal({ show: false, user: null, hardDelete: false })}
              >
                Cancel
              </button>
              <button style={styles.modalDeleteButton} onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .table-row:hover {
          background-color: #f8f9fa;
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
    padding: '20px',
    fontFamily: '"Open Sans", sans-serif',
  },
  header: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '24px 30px',
    marginBottom: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backButton: {
    backgroundColor: 'transparent',
    border: '1px solid #2c5aa0',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '16px',
    color: '#2c5aa0',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    margin: '0 0 4px 0',
    color: '#2c5aa0',
  },
  subtitle: {
    fontSize: '14px',
    margin: '0',
    color: '#666666',
  },
  createButton: {
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  },
  buttonIcon: {
    fontSize: '14px',
  },
  filtersCard: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e0e0e0',
    maxWidth: '1400px',
    margin: '0 auto 20px auto',
  },
  searchBar: {
    position: 'relative',
    marginBottom: '20px',
  },
  searchIcon: {
    position: 'absolute',
    left: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#666666',
    fontSize: '16px',
  },
  searchInput: {
    width: '100%',
    padding: '12px 12px 12px 44px',
    fontSize: '16px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontFamily: '"Open Sans", sans-serif',
    outline: 'none',
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    alignItems: 'center',
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  filterLabel: {
    fontSize: '14px',
    color: '#666666',
    fontWeight: '500',
  },
  filterSelect: {
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    fontFamily: '"Open Sans", sans-serif',
    outline: 'none',
    cursor: 'pointer',
  },
  checkboxGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  checkboxLabel: {
    fontSize: '14px',
    color: '#666666',
    fontWeight: '500',
    cursor: 'pointer',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    gap: '20px',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #2c5aa0',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: '16px',
    color: '#666666',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    gap: '20px',
  },
  errorIcon: {
    fontSize: '48px',
    color: '#dc3545',
  },
  errorText: {
    fontSize: '16px',
    color: '#666666',
  },
  retryButton: {
    backgroundColor: '#2c5aa0',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    gap: '20px',
  },
  emptyIcon: {
    fontSize: '64px',
    color: '#cccccc',
  },
  emptyText: {
    fontSize: '18px',
    color: '#666666',
  },
  tableContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e0e0e0',
    maxWidth: '1400px',
    margin: '0 auto 20px auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeaderRow: {
    backgroundColor: '#f8f9fa',
  },
  tableHeader: {
    padding: '16px',
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '600',
    color: '#333333',
    borderBottom: '2px solid #e0e0e0',
  },
  tableRow: {
    borderBottom: '1px solid #e0e0e0',
    transition: 'background-color 0.2s',
  },
  tableCell: {
    padding: '16px',
    fontSize: '14px',
    color: '#333333',
  },
  badge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white',
    display: 'inline-block',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  actionButton: {
    backgroundColor: 'transparent',
    border: 'none',
    padding: '8px 12px',
    fontSize: '14px',
    color: '#2c5aa0',
    cursor: 'pointer',
    borderRadius: '6px',
    transition: 'background-color 0.2s',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '20px 0',
  },
  paginationInfo: {
    fontSize: '14px',
    color: '#666666',
  },
  paginationButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  paginationButton: {
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    padding: '8px 12px',
    fontSize: '14px',
    color: '#2c5aa0',
    cursor: 'pointer',
    borderRadius: '6px',
    transition: 'all 0.2s',
  },
  paginationButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  paginationText: {
    fontSize: '14px',
    color: '#333333',
    fontWeight: '500',
    padding: '0 12px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '32px',
    maxWidth: '500px',
    width: '90%',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#333333',
    margin: '0 0 16px 0',
  },
  modalText: {
    fontSize: '16px',
    color: '#666666',
    margin: '0 0 24px 0',
    lineHeight: '1.5',
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px',
  },
  modalCancelButton: {
    backgroundColor: 'transparent',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#666666',
    cursor: 'pointer',
  },
  modalDeleteButton: {
    backgroundColor: '#dc3545',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '16px',
    fontWeight: '600',
    color: 'white',
    cursor: 'pointer',
  },
};

export default AdminUsers;
